import React, {useEffect, useRef, useState} from "react";
import SortableList from "../../Common/Components/Lists/SortableList";
import AdminResponseAttestModel from "../Model/Response/AdminResponseAttestModel";
import {Button, Col, Row, Spinner} from "reactstrap";
import {ADMIN_PATHS} from "../../config";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {CheckUserRole} from "../../AppSystem/Helpers/CheckUserRole";
import {CheckUserPermission} from "../../AppSystem/Helpers/CheckUserPermission";
import {useSelector} from "react-redux";
import useApiCallService from "../../Common/Helpers/ApiCall";
import {FlashMessage} from "../../Common/Helpers/FlashMessage";
import { CustomReload } from "../../AppSystem/Helpers/CustomReload";
import { useHeader } from "../../Auth/Components/Nav/HeaderContext";

const STATUS_NO_QUEUE = ['Utbetald', 'Skickat', 'Nekad'];

/** Devuelve true si la fecha (string o Date) corresponde al día en curso. */
function isToday(updatedAt) {
    if (updatedAt == null) return false;
    const d = typeof updatedAt === 'string' ? new Date(updatedAt) : updatedAt;
    if (Number.isNaN(d.getTime())) return false;
    const today = new Date();
    return d.getFullYear() === today.getFullYear() &&
           d.getMonth() === today.getMonth() &&
           d.getDate() === today.getDate();
}

/** Botón "Añadir a cola de pago": solo se muestra si hay permiso, status no es Utbetald/Skickat/Nekad, updatedAt es hoy y el GET devuelve queueStatus === 'Pending'. */
function AddToPayQueueButton({ attestId, status, updatedAt, hasPermission, onAddClick, ApiCall, t }) {
    const [queueInfo, setQueueInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const fetchedRef = useRef(false);

    const skipByStatus = STATUS_NO_QUEUE.includes(status);
    const isUpdatedToday = isToday(updatedAt);
    const shouldFetch = hasPermission && attestId && !skipByStatus && isUpdatedToday;

    useEffect(() => {
        if (!shouldFetch) {
            setLoading(false);
            return;
        }
        if (fetchedRef.current) return;
        fetchedRef.current = true;

        let cancelled = false;
        const fetchStatus = async () => {
            try {
                const apiCallOptions = {
                    method: "get",
                    url: `/pendingPayment?attestId=${encodeURIComponent(attestId)}`,
                };
                await ApiCall(apiCallOptions, (response) => {
                    if (!cancelled) setQueueInfo(response);
                }, () => {
                    if (!cancelled) setQueueInfo(null);
                });
            } catch (e) {
                if (!cancelled) setQueueInfo(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchStatus();
        return () => { cancelled = true; };
    }, [attestId]);

    if (!hasPermission || skipByStatus || !isUpdatedToday) {
        return null;
    }
    if (loading) {
        return (
            <span className="d-inline-flex align-items-center ms-1" aria-hidden="true">
                <Spinner color="success" size="sm" style={{ width: '1.25rem', height: '1.25rem' }} />
            </span>
        );
    }
    if (!queueInfo || queueInfo.queueStatus !== 'Pending') {
        return null;
    }

    return (
        <button
            type="button"
            className="rounded me-2 py-2 border-0 text-green bg-green-light p-custom-button position-relative class-add-to-pay-queue"
            onClick={() => onAddClick(attestId)}
        >
            <i className="bx bx-box pb-custom align-middle size-icon-custom-button" />
            <div className="tooltip-custom-button">
                {t('attest.addToPayQueue')}
            </div>
        </button>
    );
}

export function AdminAttestListHistory() {
    const { ApiCall } = useApiCallService();
    const navigate = useNavigate();
    const { rolesData, userData } = useSelector(state => state.auth);
    const { t } = useTranslation();
    const { setPageTitle, setPageTitleKey, setOnBack } = useHeader();
    
    useEffect(() => {
        setPageTitle(t('attest.attest'));
        setPageTitleKey('attest.attest');
        
        return () => {
            setPageTitle('');
            setPageTitleKey('');
            setOnBack(null);
        };
    }, [t]);

    const [apiResponseUserCostPlaceList, setApiResponseUserCostPlaceList] = useState(null);

    // GET USER COST PLACES
    useEffect(() => {
        const handleUserCostPlaceList  = (response) => {
            setApiResponseUserCostPlaceList(response);
        };
        const GetUserCostPlaceList = async () => {
            if (userData?.id) {
                try {
                    const apiCallOptions = {
                        method: "get",
                        url: "/usersetting/primary-costplace?id=" + userData?.id,
                    };
                    await ApiCall(apiCallOptions, handleUserCostPlaceList);
                } catch (error) {
                    console.log('error', error);
                }
            }
        };
        GetUserCostPlaceList();
    }, [userData]);

    const isSuperAdmin = CheckUserRole(rolesData, 'SuperAdmin');
    
    let permissionAddToPayQueue = false;
    // Check permission to add to pay queue
    if(CheckUserPermission("admin.attest") || CheckUserPermission("admin.attest.addtopayqueue") || CheckUserPermission("admin")){
        permissionAddToPayQueue = true;
    }

    // Function to add attest to pay queue
    const handleAddToPayQueue = async (attestId) => {
        try {
            const handleApiResponse = (response) => {
                if (response !== null) {
                    FlashMessage(t('attest.addedToPayQueue'), 'flash-messageSuccess');
                    setTimeout(() => {
                        CustomReload();
                    }, 4000);
                }
            };
            
            const apiCallOptions = {
                method: "put",
                url: "/pendingPayment",
                data: {
                    id: attestId
                },
            };
            
            await ApiCall(apiCallOptions, handleApiResponse, (error) => {
                console.log('error', error);
                FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
                setTimeout(() => {
                    CustomReload();
                }, 4000);
            });
        } catch (error) {
            console.log('error', error);
            FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
            setTimeout(() => {
                CustomReload();
            }, 4000);
        }
    };

    const renderActionColumn = (item) => {
        return (
            <>
                <button className="rounded me-2 py-2 border-0 text-green bg-green-light p-custom-button position-relative class-approve"
                        onClick={() => {
                            navigate(`${ADMIN_PATHS.ATTEST_INFO}?id=${item.id}&from=history`);
                        }} >
                    <i className="bx bx-show pb-custom align-middle size-icon-custom-button" />
                    <div className="tooltip-custom-button">
                        {t('button.info') }
                    </div>
                </button>
                <AddToPayQueueButton
                    attestId={item.id}
                    status={item.status}
                    updatedAt={item.updatedAt}
                    hasPermission={permissionAddToPayQueue}
                    onAddClick={handleAddToPayQueue}
                    ApiCall={ApiCall}
                    t={t}
                />
            </>
        );
    }

    const columns = [
        'createdAt',
        "recipient",
        "orgName",
        "costPlace",
        "model",
        "modelVisualId",
        "modelStartDate",
        "amount",
        "status",
        "approvedDeniedByName",
        "updatedAt",
    ];

    if (isSuperAdmin === true) {
        columns.unshift('id');
    }
    const ignoreFields = ["Id", "OrgId", "ModelId", "CreatedBy"];
    const listOptions = {
        "model": AdminResponseAttestModel,
        'columns':columns,
        "actions": {
            "actionColumn" : renderActionColumn,
        },
        "exportIsAvailable": true,
        "filterTestIsAvailable": true,
        "urlGetListItemsToExport": "/attest/admin/search-history/filter",
        "nameFile": t('attest.attest'),
        "modelType" : "Attest",
        "ignoreFields" :/* [...columns, "createdByName"],*/ columns,
        "urlGetSearch" : "/attest/admin/search-history/filter",
        "viewName": "AdminAttestListHistory",
    };

    return <>
        <Col lg={12} className='card card-backend p-4 mb-5'>
            <Row>
                <Col lg={6}>
                    <h2 className='rokkitt-font'>{t('attest.labelListHistory')}</h2>
                </Col>
                <Col lg={6} className="text-end">
                    {apiResponseUserCostPlaceList && apiResponseUserCostPlaceList.primaryCostPlaces ?
                        <Button className="bg-green me-3 mb-2" onClick={() => {
                            navigate(`${ADMIN_PATHS.ATTEST_LIST_PENDING_PRIMARY}`);
                        }}>
                            {t('openPayment.myItems')}
                        </Button>
                        : ''}
                    <Button className="bg-green mb-2" onClick={() => {
                        navigate(`${ADMIN_PATHS.ATTEST_LIST_PENDING}`);
                    }}>
                        {t('openPayment.otherItems')}
                    </Button>
                </Col>
            </Row>
            <SortableList
                listOptions={listOptions}
            />
        </Col>
    </>
}